/**
 * @ipc/validation · src/schemas/rbac.ts — zod-схемы (Zod v4) для границ RBAC-API.
 * Раздел 09 (RBAC). Валидирует тела запросов создания/обновления роли, назначения
 * роли администратору, создания API-ключа — до попадания в сервис.
 */
import { z } from 'zod/v4';
import { ALL_PERMISSIONS } from '@ipc/core';
import {
  paginationSchema,
  searchSchema,
  sortOrderSchema,
  booleanQuerySchema,
  apiKeyStatusSchema,
} from '../common/primitives.ts';

/** Код права: известный код из каталога, `*` (супердоступ) или `<префикс>.*` (wildcard). */
const permissionCode = z
  .string()
  .refine(
    (c) => c === '*' || ALL_PERMISSIONS.some((p) => p.code === c) || c.endsWith('.*'),
    'Неизвестный код права',
  );

export const createRoleSchema = z.object({
  name: z.string().min(2).max(50).regex(/^[a-z_]+$/, 'Только латиница и подчёркивание'),
  displayName: z.string().min(2).max(100),
  description: z.string().max(500).optional(),
  permissions: z.array(permissionCode).default([]),
});

// НЕ просто .partial(): у permissions в createRoleSchema есть .default([]) — после
// .partial() дефолт продолжает срабатывать на отсутствующем поле, и PATCH без
// permissions ЗАТИРАЛ права роли пустым массивом (найдено живым прогоном).
export const updateRoleSchema = createRoleSchema.partial().extend({
  permissions: z.array(permissionCode).optional(),
});

export const roleIdParamSchema = z.object({
  id: z.coerce.number().int().positive(),
});

/** Query GET /admin/rbac/roles — пагинация+поиск+фильтры+сортировка (эталон payments, раздел 13). */
export const roleFiltersSchema = paginationSchema.extend({
  search: searchSchema,
  isSystem: booleanQuerySchema,
  sortBy: z.enum(['createdAt', 'name', 'displayName']).default('createdAt'),
  sortOrder: sortOrderSchema,
});
export type RoleFiltersDto = z.infer<typeof roleFiltersSchema>;

export const assignRoleSchema = z.object({
  adminUserId: z.number().int().positive(),
  roleId: z.number().int().positive(),
});

export const createApiKeySchema = z.object({
  name: z.string().min(2).max(100),
  permissions: z.array(permissionCode).min(1),
  merchantId: z.number().int().positive().nullable().default(null),
  expiresAt: z.iso.datetime().optional(),
});

/** GET /admin/rbac/api-keys — page-based list (раньше грузил все строки без limit). */
export const apiKeyFiltersSchema = paginationSchema.extend({
  search: searchSchema,
  status: apiKeyStatusSchema.optional(),
  sortBy: z.enum(['createdAt', 'name']).default('createdAt'),
  sortOrder: sortOrderSchema,
});
export type ApiKeyFiltersDto = z.infer<typeof apiKeyFiltersSchema>;

export const permissionCheckParamSchema = z.object({
  permissionCode: z.string().min(1),
});

export type CreateRoleDto = z.infer<typeof createRoleSchema>;
export type UpdateRoleDto = z.infer<typeof updateRoleSchema>;
export type RoleIdParamDto = z.infer<typeof roleIdParamSchema>;
export type AssignRoleDto = z.infer<typeof assignRoleSchema>;
export type CreateApiKeyDto = z.infer<typeof createApiKeySchema>;

/**
 * Форма создания/правки роли в admin-ui: та же граница что и createRoleSchema,
 * но с русскими самодостаточными сообщениями для react-hook-form + zodResolver.
 * permissions не сверяется с каталогом ALL_PERMISSIONS на клиенте (список
 * чекбоксов уже ограничен каталогом с backend) — только непустой массив строк.
 */
export const roleFormSchema = z.object({
  name: z
    .string({ error: 'Укажите машинное имя роли' })
    .min(2, 'Машинное имя — от 2 до 50 символов')
    .max(50, 'Машинное имя — от 2 до 50 символов')
    .regex(/^[a-z_]+$/, 'Только латинские строчные буквы и подчёркивание'),
  displayName: z
    .string({ error: 'Укажите название роли' })
    .min(2, 'Название — от 2 до 100 символов')
    .max(100, 'Название — от 2 до 100 символов'),
  description: z.string().max(500, 'Описание — не более 500 символов').optional(),
  permissions: z.array(z.string()).min(1, 'Выберите хотя бы одно право'),
});
export type RoleFormDto = z.infer<typeof roleFormSchema>;

/** Форма назначения роли администратору (assignRoleSchema с русским сообщением). */
export const assignRoleFormSchema = z.object({
  adminUserId: z.number({ error: 'Выберите администратора' }).int().positive('Выберите администратора'),
  roleId: z.number({ error: 'Выберите роль' }).int().positive('Выберите роль'),
});
export type AssignRoleFormDto = z.infer<typeof assignRoleFormSchema>;

/**
 * Форма создания API-ключа: createApiKeySchema с русскими сообщениями.
 * expiresAt — datetime-local строка с клиента, конвертируется в ISO на границе api.
 */
export const apiKeyFormSchema = z.object({
  name: z
    .string({ error: 'Укажите название ключа' })
    .min(2, 'Название — от 2 до 100 символов')
    .max(100, 'Название — от 2 до 100 символов'),
  permissions: z.array(z.string()).min(1, 'Выберите хотя бы одно право'),
  merchantId: z.number().int().positive('ID мерчанта — целое положительное число').nullable(),
  expiresAt: z.string().optional(),
});
export type ApiKeyFormDto = z.infer<typeof apiKeyFormSchema>;
